import React, { useEffect, useMemo, useState } from 'react';
import { Eye, Pencil, RefreshCcw } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { superAdminGetProjects, superAdminUpdateProject } from '../../api/api';
import StyledSelect from '../../components/StyledSelect';
import {
  ActionIconButton,
  Chip,
  SuperAdminPanel,
} from '../components/SuperAdminUi';

const statusOptions = ['Active', 'On Hold', 'Completed', 'Archived'];
const priorityOptions = ['Low', 'Medium', 'High', 'Urgent'];
const currencyOptions = ['PKR', 'USD', 'EUR', 'SAR'];

const statusSelectOptions = [{ value: '', label: 'All statuses' }, ...statusOptions.map((value) => ({ value, label: value }))];

export default function SuperAdminProjects() {
  const [searchParams] = useSearchParams();
  const queryProjectId = Number(searchParams.get('projectId') || 0);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [viewRow, setViewRow] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [form, setForm] = useState({
    name: '',
    description: '',
    color: '#c44569',
    client_company: '',
    status: 'Active',
    priority: 'Medium',
    budget: '',
    currency: 'PKR',
    start_date: '',
    end_date: '',
  });

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await superAdminGetProjects({ page: 1, limit: 500 });
      setRows(res?.data?.data?.projects || []);
    } catch (requestError) {
      setRows([]);
      setError(requestError?.response?.data?.message || 'Failed to load projects.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!queryProjectId || rows.length === 0) return;
    const target = rows.find((row) => Number(row.id) === queryProjectId);
    if (target) {
      openEdit(target);
    }
  }, [queryProjectId, rows]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (statusFilter && String(row.status || '') !== statusFilter) return false;
      if (!term) return true;
      const haystack = [
        row.name,
        row.description,
        row.client_company,
        row.owner_name,
        row.owner_email,
        row.organization_name,
        row.status,
        row.priority,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [rows, search, statusFilter]);

  const openEdit = (row) => {
    setEditRow(row);
    setForm({
      name: row?.name || '',
      description: row?.description || '',
      color: row?.color || '#c44569',
      client_company: row?.client_company || '',
      status: row?.status || 'Active',
      priority: row?.priority || 'Medium',
      budget: row?.budget ?? '',
      currency: row?.currency || 'PKR',
      start_date: row?.start_date || '',
      end_date: row?.end_date || '',
    });
  };

  const saveProject = async () => {
    if (!editRow?.id) return;
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await superAdminUpdateProject(editRow.id, {
        name: form.name,
        description: form.description,
        color: form.color,
        client_company: form.client_company,
        status: form.status,
        priority: form.priority,
        budget: form.budget === '' ? null : Number(form.budget),
        currency: form.currency,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
      });
      setSuccess('Project updated successfully.');
      setEditRow(null);
      await load();
    } catch (requestError) {
      setError(requestError?.response?.data?.message || 'Failed to update project.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="superadmin-title text-3xl font-bold">Project Governance</h1>
          <p className="text-slate-500">Review and manage project records across all organizations.</p>
        </div>
        <button type="button" className="ems-btn-secondary text-sm" onClick={load}>
          <span className="inline-flex items-center gap-2"><RefreshCcw size={14} /> Refresh</span>
        </button>
      </div>

      {error ? <Banner tone="error" message={error} /> : null}
      {success ? <Banner tone="success" message={success} /> : null}

      <section className="superadmin-glass rounded-2xl p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search project, owner, organization"
            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none"
          />
          <StyledSelect
            value={statusFilter}
            onChange={setStatusFilter}
            options={statusSelectOptions}
            placeholder="Filter by status"
          />
          <button
            type="button"
            className="ems-btn-primary text-sm"
            onClick={() => {
              setSearch('');
              setStatusFilter('');
            }}
          >
            Clear Filters
          </button>
        </div>
      </section>

      <section className="superadmin-glass rounded-2xl p-4">
        <div className="overflow-auto">
          <table className="w-full min-w-[1080px] text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-left text-slate-500">
                <th className="py-2">Project</th>
                <th>Organization</th>
                <th>Owner</th>
                <th>Status</th>
                <th>Priority</th>
                <th>Budget</th>
                <th>Dates</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td className="py-8 text-center text-slate-500" colSpan={8}>Loading projects...</td>
                </tr>
              ) : filteredRows.length === 0 ? (
                <tr>
                  <td className="py-8 text-center text-slate-500" colSpan={8}>No projects matched current filters.</td>
                </tr>
              ) : filteredRows.map((row) => (
                <tr key={row.id} className="border-b border-slate-50 align-top hover:bg-slate-50/70">
                  <td className="py-3">
                    <p className="font-semibold text-slate-900">{row.name}</p>
                    <p className="text-xs text-slate-500">{row.client_company || 'No client company'}</p>
                  </td>
                  <td>{row.organization_name || 'Unassigned'}</td>
                  <td>
                    <p>{row.owner_name || '-'}</p>
                    <p className="text-xs text-slate-500">{row.owner_email || '-'}</p>
                  </td>
                  <td><Chip label={row.status || 'active'} tone={String(row.status || 'active').toLowerCase()} /></td>
                  <td>{row.priority || '-'}</td>
                  <td>{(row.currency || 'PKR')} {Number(row.budget || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                  <td>
                    <p>{row.start_date || '-'}</p>
                    <p className="text-xs text-slate-500">{row.end_date || '-'}</p>
                  </td>
                  <td>
                    <div className="inline-flex w-full justify-end gap-2">
                      <ActionIconButton icon={Eye} label="View" tone="view" onClick={() => setViewRow(row)} />
                      <ActionIconButton icon={Pencil} label="Edit" tone="edit" onClick={() => openEdit(row)} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <SuperAdminPanel
        open={!!viewRow}
        onClose={() => setViewRow(null)}
        title={viewRow ? `Project ${viewRow.name}` : ''}
        subtitle={viewRow ? `${viewRow.organization_name || 'Unassigned org'} · ${viewRow.owner_name || 'Unknown owner'}` : ''}
      >
        {viewRow ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Info label="Project Name" value={viewRow.name} />
            <Info label="Client Company" value={viewRow.client_company || '-'} />
            <Info label="Status" value={viewRow.status || '-'} />
            <Info label="Priority" value={viewRow.priority || '-'} />
            <Info label="Budget" value={`${viewRow.currency || 'PKR'} ${Number(viewRow.budget || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`} />
            <Info label="Color" value={viewRow.color || '-'} />
            <Info label="Start Date" value={viewRow.start_date || '-'} />
            <Info label="End Date" value={viewRow.end_date || '-'} />
            <Info label="Description" value={viewRow.description || '-'} />
          </div>
        ) : null}
      </SuperAdminPanel>

      <SuperAdminPanel
        open={!!editRow}
        onClose={() => setEditRow(null)}
        title={editRow ? `Edit ${editRow.name}` : ''}
        subtitle={editRow ? `${editRow.organization_name || 'Unassigned org'} · ${editRow.owner_name || 'Unknown owner'}` : ''}
        footer={(
          <div className="flex flex-wrap justify-end gap-2">
            <button type="button" className="ems-btn-secondary" onClick={() => setEditRow(null)} disabled={saving}>Cancel</button>
            <button type="button" className="ems-btn-primary" onClick={saveProject} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
          </div>
        )}
      >
        {editRow ? (
          <div className="space-y-4">
            <FormInput label="Project Name" value={form.name} onChange={(value) => setForm((prev) => ({ ...prev, name: value }))} />
            <FormInput label="Description" value={form.description} onChange={(value) => setForm((prev) => ({ ...prev, description: value }))} />
            <FormInput label="Client Company" value={form.client_company} onChange={(value) => setForm((prev) => ({ ...prev, client_company: value }))} />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormSelect label="Status" value={form.status} onChange={(value) => setForm((prev) => ({ ...prev, status: value }))} options={statusOptions} />
              <FormSelect label="Priority" value={form.priority} onChange={(value) => setForm((prev) => ({ ...prev, priority: value }))} options={priorityOptions} />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormInput label="Budget" type="number" value={form.budget} onChange={(value) => setForm((prev) => ({ ...prev, budget: value }))} />
              <FormSelect label="Currency" value={form.currency} onChange={(value) => setForm((prev) => ({ ...prev, currency: value }))} options={currencyOptions} />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormInput label="Start Date" type="date" value={form.start_date} onChange={(value) => setForm((prev) => ({ ...prev, start_date: value }))} />
              <FormInput label="End Date" type="date" value={form.end_date} onChange={(value) => setForm((prev) => ({ ...prev, end_date: value }))} />
            </div>
            <FormInput label="Color" value={form.color} onChange={(value) => setForm((prev) => ({ ...prev, color: value }))} />
          </div>
        ) : null}
      </SuperAdminPanel>
    </div>
  );
}

function FormInput({ label, value, onChange, type = 'text' }) {
  return (
    <label>
      <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>
      <input
        type={type}
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value)}
        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none"
      />
    </label>
  );
}

function FormSelect({ label, value, onChange, options }) {
  return (
    <label>
      <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>
      <StyledSelect
        value={value}
        onChange={onChange}
        options={(options || []).map((item) => ({ value: item, label: item }))}
      />
    </label>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-[0.12em] text-slate-400">{label}</p>
      <p className="mt-1 text-sm font-medium text-slate-700">{value}</p>
    </div>
  );
}

function Banner({ tone, message }) {
  const style = tone === 'error'
    ? { background: '#fef2f2', borderColor: '#fecaca', color: '#b91c1c' }
    : { background: '#f0fdf4', borderColor: '#bbf7d0', color: '#15803d' };

  return (
    <div className="rounded-2xl border px-4 py-3 text-sm font-medium" style={style}>
      {message}
    </div>
  );
}
